import { Grid } from '@mui/material';
import { DealExcludedLender } from '../../shared/models';
import {
	CerebroHeading,
	CerebroIcon,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
	CerebroTextButton,
} from '../../shared/components';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { BooleanUtils } from '../../shared/utils';

interface ExcludedLenderDisplayProps {
	lenders: DealExcludedLender[];
	invalid: boolean | undefined;
	onDelete: (lender: DealExcludedLender) => void;
	onAdd: (show: boolean) => void;
	isEmployeeUser: boolean;
	hasDealLaunched: boolean;
	excludedLenderCount: number;
}

export const ExcludedLenderDisplay: React.FC<ExcludedLenderDisplayProps> = ({
	lenders,
	invalid,
	onDelete,
	onAdd,
	isEmployeeUser,
	hasDealLaunched,
	excludedLenderCount,
}) => {
	const canAdd = isEmployeeUser || ((lenders == null || lenders.length < excludedLenderCount) && !hasDealLaunched);
	const limitReached = !isEmployeeUser && lenders != null && lenders.length >= excludedLenderCount;
	const canDelete = !hasDealLaunched || isEmployeeUser;

	return lenders && lenders.length > 0 ? (
		<>
			<Grid container spacing={2} sx={{ marginY: 3 }}>
				<Grid item xs={7}>
					<CerebroLabel>Lender</CerebroLabel>
				</Grid>
				<Grid item xs={3}>
					<CerebroLabel>Incumbent?</CerebroLabel>
				</Grid>
				<Grid item xs={2}></Grid>
				{lenders.map(lender => (
					<React.Fragment key={lender.guid}>
						<Grid item xs={7}>
							<CerebroText variant="small">{lender.bankName}</CerebroText>
						</Grid>
						<Grid item xs={3}>
							<CerebroText variant="small">{BooleanUtils.YesNo(lender.isIncumbentLender)}</CerebroText>
						</Grid>
						{canDelete && (
							<Grid item xs={2}>
								<CerebroTextButton
									color="error"
									title={`Remove exclusion for ${lender.bankName}`}
									onClick={() => onDelete(lender)}
								>
									Remove
									<CerebroIcon padLeft>
										<FontAwesomeIcon icon={faTrash} size="sm" />
									</CerebroIcon>
								</CerebroTextButton>
							</Grid>
						)}
					</React.Fragment>
				))}

				{canAdd && (
					<Grid item xs={12}>
						<CerebroTextButton size="small" variant="outlined" onClick={() => onAdd(true)}>
							Exclude Another Lender
						</CerebroTextButton>
					</Grid>
				)}
				{isEmployeeUser && (
					<Grid item xs={12}>
						<CerebroText variant="hint">
							Excluded Lender Limit: <strong>{excludedLenderCount}</strong>
						</CerebroText>
					</Grid>
				)}
				{limitReached && (
					<Grid item xs={12}>
						<CerebroText variant="hint">
							* No additional lenders can be added to the excluded lender list.
						</CerebroText>
					</Grid>
				)}
			</Grid>

			<hr />
		</>
	) : (
		<>
			<CerebroStyledBox sx={{ marginY: 6 }} error={invalid}>
				<CerebroHeading size="h2" component="h2" sx={{ marginBottom: 4 }}>
					You have not excluded any lenders from the loan yet.
				</CerebroHeading>
				<Grid container justifyContent="center">
					<Grid item>
						<CerebroPrimaryButton wide onClick={onAdd}>
							Exclude Lender
						</CerebroPrimaryButton>
					</Grid>
				</Grid>
				{isEmployeeUser && (
					<Grid item xs={12}>
						<CerebroText variant="hint">
							Excluded Lender Limit: <strong>{excludedLenderCount}</strong>
						</CerebroText>
					</Grid>
				)}
				{invalid && (
					<CerebroText variant="error" sx={{ marginTop: 4 }}>
						You indicated that you have lenders to exclude but did not specify any lender to exclude from
						the loan. Please exclude at least one lender from the loan.
					</CerebroText>
				)}
			</CerebroStyledBox>
		</>
	);
};
